/**
 * Execution List - Browse pipeline executions
 */

import React, { useEffect, useState, useMemo } from 'react';
import pipelineApi from '../api/client';
import type { PipelineExecution } from '../types/pipeline';
import { ExecutionWizard } from './ExecutionWizard';
import { ArrowLeftIcon, CheckIcon, CircleIcon, CloseIcon, PlayIcon } from './icons';
import './ExecutionList.css';

interface ExecutionListProps {
  onOpenExecution: (executionId: string) => void;
  pipelineId?: string;
  onBack?: () => void;
}

type SortField = 'started_at' | 'status' | 'pipeline_name';
type SortDirection = 'asc' | 'desc';

const PAGE_SIZE = 25;

export const ExecutionList: React.FC<ExecutionListProps> = ({ onOpenExecution, pipelineId, onBack }) => {
  const [executions, setExecutions] = useState<PipelineExecution[]>([]);
  const [pipelineMap, setPipelineMap] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortField, setSortField] = useState<SortField>('started_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [rerunExecution, setRerunExecution] = useState<PipelineExecution | null>(null);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  useEffect(() => {
    loadExecutions();
    const interval = setInterval(() => loadExecutions(false), 30000);
    return () => clearInterval(interval);
  }, [page, pipelineId]);

  // Deleting the last execution(s) can leave us past the final page
  useEffect(() => {
    if (page > 0 && page >= pageCount) setPage(pageCount - 1);
  }, [page, pageCount]);

  const loadExecutions = async (showLoading = true) => {
    if (showLoading) setLoading(true);
    setError(null);
    try {
      const [pipelines, executionPage] = await Promise.all([
        pipelineApi.listPipelines(),
        pipelineApi.listExecutions(PAGE_SIZE, page * PAGE_SIZE, pipelineId),
      ]);

      const nameMap: Record<string, string> = {};
      for (const pipeline of pipelines) {
        nameMap[pipeline.id] = pipeline.metadata.name;
      }

      setPipelineMap(nameMap);
      setExecutions(executionPage.executions);
      setTotal(executionPage.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load executions');
      console.error('Failed to load executions:', err);
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString();
  };

  const getStatusClass = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return 'status-completed';
      case 'failed':
        return 'status-failed';
      case 'running':
        return 'status-running';
      default:
        return 'status-pending';
    }
  };

  const filteredAndSortedExecutions = useMemo(() => {
    // Workflow scoping (pipelineId) and pagination happen server-side
    let filtered = executions;

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(exec => exec.status.toLowerCase() === statusFilter);
    }

    // Filter by search query (pipeline name or execution id)
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(exec => {
        const pipelineName = pipelineMap[exec.pipeline_id]?.toLowerCase() || '';
        const execId = exec.id.toLowerCase();
        return pipelineName.includes(query) || execId.includes(query);
      });
    }

    // Sort
    const sorted = [...filtered].sort((a, b) => {
      let comparison = 0;

      if (sortField === 'started_at') {
        comparison = new Date(a.started_at).getTime() - new Date(b.started_at).getTime();
      } else if (sortField === 'status') {
        comparison = a.status.localeCompare(b.status);
      } else if (sortField === 'pipeline_name') {
        const nameA = pipelineMap[a.pipeline_id] || '';
        const nameB = pipelineMap[b.pipeline_id] || '';
        comparison = nameA.localeCompare(nameB);
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return sorted;
  }, [executions, pipelineMap, searchQuery, statusFilter, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const handleRerun = (execution: PipelineExecution, event: React.MouseEvent) => {
    event.stopPropagation();
    setRerunExecution(execution);
  };

  const handleDelete = async (execution: PipelineExecution, event: React.MouseEvent) => {
    event.stopPropagation();
    if (!confirm(`Are you sure you want to delete this execution? This cannot be undone.`)) {
      return;
    }
    try {
      await pipelineApi.deleteExecution(execution.id);
      await loadExecutions();
    } catch (err) {
      setError('Failed to delete execution: ' + (err instanceof Error ? err.message : 'Unknown error'));
    }
  };

  if (loading) {
    return (
      <div className="execution-list-container">
        <div className="execution-list-loading">Loading executions...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="execution-list-container">
        <div className="execution-list-error">
          <strong>Error:</strong> {error}
          <button className="button-secondary" onClick={() => loadExecutions()}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="execution-list-container">
      <div className="execution-list-header">
        <div>
          <h1>Executions</h1>
          <p className="text-secondary">
            {pipelineId
              ? `Showing runs for ${pipelineMap[pipelineId] || 'this workflow'}`
              : 'Monitor your workflow execution history'}
          </p>
        </div>
        {onBack && (
          <button className="button-secondary" onClick={onBack}>
            <ArrowLeftIcon /> Back to workflow
          </button>
        )}
      </div>

      <div className="execution-filters">
        <input
          type="text"
          className="search-input"
          placeholder="Search by workflow name or execution ID..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <select
          className="filter-select"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="running">Running</option>
          <option value="completed">Completed</option>
          <option value="failed">Failed</option>
        </select>
      </div>

      {filteredAndSortedExecutions.length === 0 && total === 0 ? (
        <div className="execution-list-empty">
          <h3>No executions yet</h3>
          <p>Execute a workflow to see its history here</p>
        </div>
      ) : filteredAndSortedExecutions.length === 0 ? (
        <div className="execution-list-empty">
          <p>No executions on this page match your filters</p>
        </div>
      ) : (
        <div className="execution-table">
          <div className="execution-table-header">
            <div className="execution-col-status" onClick={() => handleSort('status')}>
              Status {sortField === 'status' && (sortDirection === 'asc' ? '↑' : '↓')}
            </div>
            <div className="execution-col-pipeline" onClick={() => handleSort('pipeline_name')}>
              Workflow {sortField === 'pipeline_name' && (sortDirection === 'asc' ? '↑' : '↓')}
            </div>
            <div className="execution-col-id">Execution ID</div>
            <div className="execution-col-progress">Progress</div>
            <div className="execution-col-nodes">Nodes</div>
            <div className="execution-col-started" onClick={() => handleSort('started_at')}>
              Started {sortField === 'started_at' && (sortDirection === 'asc' ? '↑' : '↓')}
            </div>
            <div className="execution-col-completed">Completed</div>
            <div className="execution-col-actions">Actions</div>
          </div>
          {filteredAndSortedExecutions.map(execution => {
            const nodeStatuses = Object.values(execution.node_executions);
            const completedNodes = nodeStatuses.filter(ne => ne.status === 'completed').length;
            const failedNodes = nodeStatuses.filter(ne => ne.status === 'failed').length;
            const runningNodes = nodeStatuses.filter(ne => ne.status === 'running').length;
            const pendingNodes = nodeStatuses.filter(ne => ne.status === 'pending').length;
            const totalNodes = nodeStatuses.length;

            return (
              <div
                key={execution.id}
                className="execution-row"
              >
                <div className="execution-col-status" onClick={() => onOpenExecution(execution.id)}>
                  <div className={`execution-status ${getStatusClass(execution.status)}`}>
                    <span className="status-dot" />
                    <span className="status-text">{execution.status.toLowerCase()}</span>
                  </div>
                </div>
                <div className="execution-col-pipeline" onClick={() => onOpenExecution(execution.id)}>
                  {pipelineMap[execution.pipeline_id] || 'Unknown Workflow'}
                </div>
                <div className="execution-col-id" onClick={() => onOpenExecution(execution.id)}>
                  {execution.id.slice(0, 8)}
                </div>
                <div className="execution-col-progress" onClick={() => onOpenExecution(execution.id)}>
                  <div className="progress-bar">
                    <div
                      className={`progress-fill ${getStatusClass(execution.status)}`}
                      style={{ width: `${totalNodes ? (completedNodes / totalNodes) * 100 : 0}%` }}
                    />
                  </div>
                  <span className="progress-text">
                    {completedNodes} of {totalNodes} nodes
                  </span>
                </div>
                <div className="execution-col-nodes" onClick={() => onOpenExecution(execution.id)}>
                  <div className="node-status-badges">
                    {completedNodes > 0 && (
                      <span className="node-badge node-badge-completed">
                        <CheckIcon size={12} /> {completedNodes}
                      </span>
                    )}
                    {failedNodes > 0 && (
                      <span className="node-badge node-badge-failed">
                        <CloseIcon size={12} /> {failedNodes}
                      </span>
                    )}
                    {runningNodes > 0 && (
                      <span className="node-badge node-badge-running">
                        <PlayIcon size={12} /> {runningNodes}
                      </span>
                    )}
                    {pendingNodes > 0 && (
                      <span className="node-badge node-badge-pending">
                        <CircleIcon size={12} /> {pendingNodes}
                      </span>
                    )}
                  </div>
                </div>
                <div className="execution-col-started" onClick={() => onOpenExecution(execution.id)}>
                  {formatDate(execution.started_at)}
                </div>
                <div className="execution-col-completed" onClick={() => onOpenExecution(execution.id)}>
                  {execution.completed_at ? formatDate(execution.completed_at) : '-'}
                </div>
                <div className="execution-col-actions">
                  <button
                    className="execution-rerun-button"
                    onClick={(e) => handleRerun(execution, e)}
                    title="Re-run pipeline"
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                      <path d="M4 2l10 6-10 6V2z"/>
                    </svg>
                  </button>
                  <button
                    className="execution-delete-button"
                    onClick={(e) => handleDelete(execution, e)}
                    title="Delete execution"
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                      <path d="M6 2h4a1 1 0 011 1v1h3v1.5H2V4h3V3a1 1 0 011-1zm-1.5 4h7l-.5 8.5a1 1 0 01-1 .95H6a1 1 0 01-1-.95L4.5 6z"/>
                    </svg>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {total > PAGE_SIZE && (
        <div className="execution-pagination">
          <button
            className="button-secondary"
            onClick={() => setPage(p => p - 1)}
            disabled={page === 0}
          >
            Previous
          </button>
          <span className="text-secondary">
            Page {page + 1} of {pageCount} · {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}
          </span>
          <button
            className="button-secondary"
            onClick={() => setPage(p => p + 1)}
            disabled={page >= pageCount - 1}
          >
            Next
          </button>
        </div>
      )}

      {rerunExecution && (
        <ExecutionWizard
          pipelineId={rerunExecution.pipeline_id}
          pipelineName={pipelineMap[rerunExecution.pipeline_id]}
          onClose={() => setRerunExecution(null)}
          onOpenExecution={(executionId) => {
            setRerunExecution(null);
            onOpenExecution(executionId);
          }}
        />
      )}
    </div>
  );
};
